import dayjs from 'dayjs';

import { Schemas } from '@api-client/generated/types';

type TaxType = Schemas.AdminObligationResponseDto['type'];
type TaxPeriod = Schemas.AdminObligationResponseDto['period'];
type TaxStatus = Schemas.AdminObligationResponseDto['status'];

export const TAX_TYPE_NAMES: Record<TaxType, string> = {
  chamber_of_commerce_fee: 'Chamber of Commerce Fee',
  vat_return_declaration: 'VAT Return',
  annual_vat_return_declaration: 'Annual VAT Return',
  annual_accounts: 'Annual Accounts',
  model_500: 'Model 500',
  corporate_income_tax: 'Corporate Income Tax',
  communal_business_tax: 'Communal Business Tax',
  wealth_tax: 'Wealth Tax',
};

export const TAX_STATUS_NAMES: Record<TaxStatus, string> = {
  not_started: 'Not Started',
  waiting_for_submission: 'Waiting for Submission',
  waiting_for_client_approval: 'Waiting for Approval',
  approved: 'Approved',
  filling_to_authority: 'Filing to Authority',
  waiting_for_payment: 'Waiting for Payment',
  submitted: 'Submitted',
  paid: 'Paid',
  done: 'Completed',
};

export const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/**
 * Formats a tax name based on its type and period
 *
 * @param taxType The type of tax
 * @param taxPeriod The period object (annual, quarterly, or monthly)
 * @returns A formatted tax name string
 */
export const formatTaxName = (
  taxType: TaxType,
  taxPeriod: TaxPeriod
): string => {
  // Get the human-readable name for this tax type
  const taxName = TAX_TYPE_NAMES[taxType] || taxType;

  if (!taxPeriod || !taxPeriod.year) {
    return taxName;
  }

  // Format based on period type
  if (taxPeriod.period === 'annual') {
    // Annual tax: "Annual Accounts 2025"
    return `${taxName} ${taxPeriod.year}`;
  } else if (taxPeriod.period === 'quarter' && 'quarter' in taxPeriod) {
    // Quarterly tax: "VAT Return Q1 2025"
    return `${taxName} Q${taxPeriod.quarter + 1} ${taxPeriod.year}`;
  } else if (taxPeriod.period === 'month' && 'month' in taxPeriod) {
    // Monthly tax: "VAT Return January 2025"
    const monthName = MONTH_NAMES[taxPeriod.month];
    return `${taxName} ${monthName} ${taxPeriod.year}`;
  }

  // Fallback for unknown period types
  return taxName;
};

// Tax status formatting information
type StatusInfo = {
  name: string;
  color: 'secondary' | 'warning' | 'success';
};

/**
 * Returns formatted information about a tax status
 * including display name and color
 *
 * @param status The tax status
 * @returns An object with name and color properties
 */
export const formatTaxStatus = (status: TaxStatus): StatusInfo => {
  const name = TAX_STATUS_NAMES[status] || status;
  let color: StatusInfo['color'];
  switch (status) {
    case 'not_started':
    case 'waiting_for_submission':
      color = 'secondary';
      break;

    case 'paid':
    case 'done':
      color = 'success';
      break;

    default:
      color = 'warning';
  }

  return { name, color };
};

/**
 * Formats a date to DD-MM-YYYY string format
 *
 * @param date A dayjs object or a date that can be parsed by dayjs
 * @returns A string in DD-MM-YYYY format
 */
export const formatDateToString = (
  date: dayjs.Dayjs | string | Date | null | undefined
): string => {
  if (!date) return '-';
  return dayjs(date).format('YYYY-MM-DD');
};
